import { getArticlesSupport } from '@bridebook/web/app-basic/lib/article/selectors';
import sortBy from 'lodash/sortBy';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { addCountryToSearchUrl } from '@bridebook/toolbox/src/add-country-to-search-url';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import {
  NavigationOptionVariant,
  type TNavigationOption,
  TNavigationOptionLink,
  type TNavigationOptions,
} from '@bridebook/web/app-basic/components/navigation-modal/nav-list/types';
import { isNavigationOptionWithChildren } from '@bridebook/web/app-basic/components/navigation-modal/nav-list/utils';
import { Icon } from '@bridebook/aura';
import {
  AeroplaneHeart,
  BuildingsTown,
  CheckList,
  DesktopHeart,
  DiscoBall,
} from '@bridebook/aura/src/icons';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { formatArea } from '@bridebook/web/app-shared/lib/search/utils/format-area';
import { getArticleCategoriesList } from '@bridebook/web/lib/articles/constants/articleCategories';

const VENUES_ORDER = {
  [CountryCodes.GB]: [
    'country-houses',
    'barns',
    'hotels',
    'castles',
    'pubs-restaurants',
    'outdoor-venues',
    'city-venues',
    'conference-centres',
    'cruises-boats-yachts',
    'golf-courses',
    'museums-attractions',
    'places-of-worship',
    'stately-homes',
    'town-halls-registry-offices',
    'warehouses-factories',
    'view-all-wedding-venues',
  ],
  [CountryCodes.DE]: [
    'castles',
    'barns',
    'country-houses',
    'hotels',
    'pubs-restaurants',
    'city-venues',
    'museums-attractions',
    'golf-courses',
    'conference-centres',
    'town-halls-registry-offices',
    'warehouses-factories',
    'outdoor-venues',
    'cruises-boats-yachts',
    'view-all-wedding-venues',
  ],
  [CountryCodes.FR]: [
    'country-houses',
    'castles',
    'conference-centres',
    'hotels',
    'barns',
    'pubs-restaurants',
    'cruises-boats-yachts',
    'town-halls-registry-offices',
    'museums-attractions',
    'places-of-worship',
    'view-all-wedding-venues',
  ],
  [CountryCodes.IE]: [
    'country-houses',
    'barns',
    'hotels',
    'castles',
    'pubs-restaurants',
    'museums-attractions',
    'conference-centres',
    'view-all-wedding-venues',
  ],
};

const SUPPLIERS_ORDER = {
  [CountryCodes.GB]: [
    'photographers',
    'florists',
    'music',
    'videographers',
    'catering',
    'entertainment',
    'cakes',
    'wedding-dresses',
    'menswear',
    'wedding-rings',
    'planners-celebrants',
    'beauty-hair-makeup',
    'stationery',
    'transport',
    'decoration-hire',
    'marquee-hire',
    'view-all-wedding-suppliers',
  ],
  [CountryCodes.DE]: [
    'photographers',
    'florists',
    'music',
    'catering',
    'cakes',
    'wedding-dresses',
    'wedding-rings',
    'stationery',
    'beauty-hair-makeup',
    'transport',
    'view-all-wedding-suppliers',
  ],
};

const MARKETS_USING_PREFIX = [CountryCodes.GB];

type TNavigationOptionExtras = {
  countries?: CountryCodes[];
  visible?: boolean;
};
type TNavigationOptionConstant = TNavigationOption & TNavigationOptionExtras;

export const useBurgerMenuOptions = (): TNavigationOptions => {
  const { t } = useTranslation('common');
  const articlesSupport = useSelector(getArticlesSupport);
  const market = useMarket();
  // example using prefix: /uk, example not using prefix: /deutschland--de
  const location = MARKETS_USING_PREFIX.includes(market.country)
    ? market.prefix
    : addCountryToSearchUrl(formatArea(market.getCountryName()), market.country);
  const filterOptionsForOtherMarkets = useCallback(
    (option: TNavigationOptionConstant) =>
      (!option.countries || option.countries.includes(market.country)) &&
      (!('visible' in option) || option.visible),
    [market.country],
  );
  const mapOptionConstantToOption = useCallback(
    ({ countries, visible, ...option }: TNavigationOptionConstant): TNavigationOption => {
      const addMarketToUrl = (href: string) => `/${market.prefix}/${href}`;
      if (isNavigationOptionWithChildren(option)) {
        return {
          ...option,
          href: option.href ? addMarketToUrl(option.href) : undefined,
        };
      }

      return {
        ...option,
        href: option.href ? addMarketToUrl(option.href) : undefined,
      } as TNavigationOptionLink & TNavigationOptionExtras;
    },
    [market.prefix],
  );

  return useMemo(
    () =>
      [
        {
          id: 'venues',
          text: t('navigation.weddingVenues'),
          icon: <Icon icon={BuildingsTown} size={30} />,
          href: 'search/wedding-venues',
          children: sortBy(
            [
              {
                id: 'affordable',
                text: t('navigation.weddingVenues.affordable'),
                href: `search/wedding-venues/${location}/affordable`,
                countries: [CountryCodes.GB, CountryCodes.DE, CountryCodes.FR, CountryCodes.IE],
              },
              {
                id: 'barn',
                text: t('navigation.weddingVenues.barn'),
                href: `search/wedding-venues/${location}/barn`,
                countries: [CountryCodes.GB, CountryCodes.DE, CountryCodes.FR, CountryCodes.IE],
              },
              {
                id: 'castle',
                text: t('navigation.weddingVenues.castle'),
                href: `search/wedding-venues/${location}/castle`,
                countries: [CountryCodes.GB, CountryCodes.DE, CountryCodes.FR, CountryCodes.IE],
              },
              {
                id: 'country-house',
                text: t('navigation.weddingVenues.countryHouseManorHouse'),
                href: `search/wedding-venues/${location}/countryHouseManorHouse`,
                countries: [CountryCodes.GB, CountryCodes.DE, CountryCodes.FR, CountryCodes.IE],
              },
              {
                id: 'hotel',
                text: t('navigation.weddingVenues.hotel'),
                href: `search/wedding-venues/${location}/hotel`,
                countries: [CountryCodes.GB, CountryCodes.DE, CountryCodes.FR, CountryCodes.IE],
              },
              {
                id: 'intimate',
                text: t('navigation.weddingVenues.intimate'),
                href: `search/wedding-venues/${location}/intimate`,
                countries: [CountryCodes.GB, CountryCodes.DE, CountryCodes.FR, CountryCodes.IE],
              },
              {
                id: 'luxury',
                text: t('navigation.weddingVenues.luxury'),
                href: `search/wedding-venues/${location}/luxury`,
                countries: [CountryCodes.GB, CountryCodes.DE, CountryCodes.FR, CountryCodes.IE],
              },
              {
                id: 'outdoor',
                text: t('navigation.weddingVenues.gardenOutdoor'),
                href: `search/wedding-venues/${location}/gardenOutdoor`,
                countries: [CountryCodes.GB, CountryCodes.DE],
              },
              {
                id: 'pub-restaurant',
                text: t('navigation.weddingVenues.pubRestaurant'),
                href: `search/wedding-venues/${location}/pubRestaurant`,
                countries: [CountryCodes.GB, CountryCodes.DE, CountryCodes.FR, CountryCodes.IE],
              },
              {
                id: 'view-all-wedding-venues',
                text: t('navigation.weddingVenues.viewAll'),
                href: 'search/wedding-venues',
                variant: NavigationOptionVariant.Link,
                countries: [CountryCodes.GB, CountryCodes.DE, CountryCodes.FR, CountryCodes.IE],
              },
            ]
              .filter(filterOptionsForOtherMarkets)
              .map(mapOptionConstantToOption),
            (item) =>
              market.country in VENUES_ORDER
                ? (VENUES_ORDER as any)[market.country].indexOf(item.id)
                : undefined,
          ),
        },
        {
          id: 'suppliers',
          text: t('navigation.weddingSuppliers'),
          icon: <Icon icon={DiscoBall} size={30} />,
          countries: [CountryCodes.GB, CountryCodes.DE],
          children: sortBy(
            [
              {
                id: 'photographers',
                text: t('navigation.weddingSuppliers.photographer_plural'),
                href: `search/wedding-photographers/${location}`,
              },
              {
                id: 'florists',
                text: t('navigation.weddingSuppliers.florist_plural'),
                href: `search/wedding-florists/${location}`,
              },
              {
                id: 'music',
                text: t('navigation.weddingSuppliers.music_plural'),
                href: `search/wedding-musicians-bands-djs/${location}`,
              },
              {
                id: 'videographers',
                text: t('navigation.weddingSuppliers.videographer_plural'),
                href: `search/wedding-videographers/${location}`,
                countries: [CountryCodes.GB],
              },
              {
                id: 'catering',
                text: t('navigation.weddingSuppliers.catering_plural'),
                href: `search/wedding-catering/${location}`,
              },
              {
                id: 'entertainment',
                text: t('navigation.weddingSuppliers.entertainment_plural'),
                href: `search/wedding-entertainment/${location}`,
                countries: [CountryCodes.GB],
              },
              {
                id: 'cakes',
                text: t('navigation.weddingSuppliers.cake_plural'),
                href: `search/wedding-cakes/${location}`,
              },
              {
                id: 'wedding-dresses',
                text: t('navigation.weddingSuppliers.dress_plural'),
                href: `search/wedding-dress-accessories/${location}`,
              },
              {
                id: 'menswear',
                text: t('navigation.weddingSuppliers.menswear_plural'),
                href: `search/wedding-menswear/${location}`,
                countries: [CountryCodes.GB],
              },
              {
                id: 'wedding-rings',
                text: t('navigation.weddingSuppliers.weddingRing_plural'),
                href: `search/wedding-rings-jewellery/${location}`,
              },
              {
                id: 'planners-celebrants',
                text: t('navigation.weddingSuppliers.planner_plural'),
                href: `search/wedding-planners-toastmasters-celebrants/${location}`,
                countries: [CountryCodes.GB],
              },
              {
                id: 'beauty-hair-makeup',
                text: t('navigation.weddingSuppliers.beauty_plural'),
                href: `search/wedding-beauty-hair-makeup/${location}`,
              },
              {
                id: 'stationery',
                text: t('navigation.weddingSuppliers.stationery_plural'),
                href: `search/wedding-stationery/${location}`,
              },
              {
                id: 'transport',
                text: t('navigation.weddingSuppliers.transport_plural'),
                href: `search/wedding-transport/${location}`,
              },
              {
                id: 'decoration-hire',
                text: t('navigation.weddingSuppliers.decoration_plural'),
                href: `search/wedding-decoration-hire/${location}`,
                countries: [CountryCodes.GB],
              },
              {
                id: 'marquee-hire',
                text: t('navigation.weddingSuppliers.marquee_plural'),
                href: `search/wedding-marquee-hire/${location}`,
                countries: [CountryCodes.GB],
              },
              {
                id: 'view-all-wedding-suppliers',
                text: t('navigation.weddingSuppliers.viewAll'),
                href: 'search',
                variant: NavigationOptionVariant.Link,
              },
            ]
              .filter(filterOptionsForOtherMarkets)
              .map(mapOptionConstantToOption),
            (item) =>
              market.country in SUPPLIERS_ORDER
                ? (SUPPLIERS_ORDER as any)[market.country].indexOf(item.id)
                : undefined,
          ),
        },
        {
          id: 'wedding-websites',
          text: t('navigation.weddingWebsites'),
          href: 'wedding-website',
          icon: <Icon icon={DesktopHeart} size={30} />,
          variant: NavigationOptionVariant.SingleWithChevron,
        },
        {
          id: 'planning-tools',
          text: t('navigation.planningTools'),
          icon: <Icon icon={CheckList} size={30} />,
          children: [
            {
              id: 'checklist',
              text: t('navigation.planningTools.checklist'),
              href: 'wedding-planning-tools/checklist',
            },
            {
              id: 'budget',
              text: t('navigation.planningTools.budget'),
              href: 'wedding-planning-tools/budget',
            },
            {
              id: 'guest-list',
              text: t('navigation.planningTools.guestList'),
              href: 'wedding-planning-tools/guestlist',
            },
            {
              id: 'all-planning-tools',
              text: t('navigation.planningTools.viewAll'),
              href: 'wedding-planning-tools',
              variant: NavigationOptionVariant.Link,
            },
          ]
            .filter(filterOptionsForOtherMarkets)
            .map(mapOptionConstantToOption),
        },
        {
          id: 'advice',
          text: t('navigation.advice'),
          icon: <Icon icon={AeroplaneHeart} size={30} />,
          href: 'advice',
          visible: articlesSupport,
          children: [
            ...getArticleCategoriesList()
              .filter(({ value }) => value !== '')
              .map(({ id, label }) => ({
                id,
                text: label,
                href: `advice/${id}`,
              })),
            {
              id: 'view-all-advice',
              text: t('navigation.advice.viewAll'),
              href: 'advice',
              variant: NavigationOptionVariant.Link,
            },
          ]
            .filter(filterOptionsForOtherMarkets)
            .map(mapOptionConstantToOption),
        },
      ]
        .filter(filterOptionsForOtherMarkets)
        .map(mapOptionConstantToOption),
    [
      articlesSupport,
      filterOptionsForOtherMarkets,
      location,
      mapOptionConstantToOption,
      market.country,
      t,
    ],
  );
};
